import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import LinkIcon from "@mui/icons-material/Link";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import appConfig from "../../shared/js/app-config";
import PaymentConfigService from "../../services/PaymentConfigService";
import ServicesService from "../../services/ServicesService";

const PROVIDER_OPTIONS = [
  { value: "razorpay", label: "Razorpay" },
  { value: "payu", label: "PayU" },
  { value: "zaakpay", label: "Zaakpay" },
  { value: "upi_vpa", label: "UPI VPA" },
];

const STATUS_OPTIONS = [
  { value: "Active", label: "Active" },
  { value: "Needs_Connecting", label: "Needs Connecting" },
  { value: "Needs_Testing", label: "Needs Testing" },
];

const GATEWAY_PROVIDERS = ["razorpay", "payu", "zaakpay"];
const USAGE_ORDER_DETAILS = "order_details";
const USAGE_PAYMENT_LINK = "payment_link";

const SERVICE_ID = () => appConfig.getServiceId();

export default function PaymentConfiguration() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [onboardingMode, setOnboardingMode] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    usage_scope: USAGE_ORDER_DETAILS,
    provider_name: "razorpay",
    configuration_name: "",
    purpose_code: "00",
    merchant_category_code: "0000",
    status: "Needs_Connecting",
    merchant_vpa: "",
    data_endpoint_url: "",
    key_id: "",
    key_secret: "",
  });
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [regenerateOpen, setRegenerateOpen] = useState(false);
  const [regenerateConfig, setRegenerateConfig] = useState(null);
  const [regenerateSubmitting, setRegenerateSubmitting] = useState(false);
  const [dataEndpointOpen, setDataEndpointOpen] = useState(false);
  const [dataEndpointConfig, setDataEndpointConfig] = useState(null);
  const [dataEndpointUrl, setDataEndpointUrl] = useState("");
  const [dataEndpointSubmitting, setDataEndpointSubmitting] = useState(false);
  const [removeConfirm, setRemoveConfirm] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [termsOfServiceDialog, setTermsOfServiceDialog] = useState({
    open: false,
    message: "",
    link: null,
  });
  const [editCredentialsOpen, setEditCredentialsOpen] = useState(false);
  const [editCredentialsConfig, setEditCredentialsConfig] = useState(null);
  const [editCredentialsForm, setEditCredentialsForm] = useState({ key_id: "", key_secret: "" });
  const [editCredentialsSubmitting, setEditCredentialsSubmitting] = useState(false);
  const isBusinessAppOnboarding = String(onboardingMode || "").toLowerCase() === "business_app";
  const canUseOrderDetails = !isBusinessAppOnboarding;

  const fetchList = () => {
    const serviceId = SERVICE_ID();
    if (!serviceId) {
      setList([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all([
      PaymentConfigService.listLocal(serviceId),
      ServicesService.getWhatsapp().catch(() => null),
    ])
      .then(([data, whatsapp]) => {
        setList(Array.isArray(data) ? data : []);
        setOnboardingMode(whatsapp?.getOnboardingMode?.() || "");
      })
      .catch((err) => {
        setSnackbar({ open: true, message: err?.response?.data?.error?.message || "Failed to load", severity: "error" });
        setList([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchList();
  }, []);

  const handleCreateOpen = () => {
    setCreateForm({
      usage_scope: canUseOrderDetails ? USAGE_ORDER_DETAILS : USAGE_PAYMENT_LINK,
      provider_name: "razorpay",
      configuration_name: "",
      purpose_code: "00",
      merchant_category_code: "0000",
      status: "Needs_Connecting",
      merchant_vpa: "",
      data_endpoint_url: "",
      key_id: "",
      key_secret: "",
    });
    setCreateOpen(true);
  };

  const handleCreateSubmit = () => {
    const {
      usage_scope,
      provider_name,
      configuration_name,
      purpose_code,
      merchant_category_code,
      status,
      merchant_vpa,
      data_endpoint_url,
      key_id,
      key_secret,
    } = createForm;
    const usageScope = usage_scope || USAGE_ORDER_DETAILS;
    if (!configuration_name?.trim()) {
      setSnackbar({ open: true, message: "Configuration name is required", severity: "warning" });
      return;
    }
    if (usageScope === USAGE_ORDER_DETAILS && !canUseOrderDetails) {
      setSnackbar({
        open: true,
        message: "WhatsApp Business App numbers support payment links only. Native order details is available for new API numbers.",
        severity: "warning",
      });
      return;
    }
    if (provider_name === "upi_vpa" && !merchant_vpa?.trim()) {
      setSnackbar({ open: true, message: "Merchant UPI VPA is required for UPI VPA", severity: "warning" });
      return;
    }
    if (usageScope === USAGE_PAYMENT_LINK && provider_name === "razorpay" && (!key_id?.trim() || !key_secret?.trim())) {
      setSnackbar({ open: true, message: "Key ID and Key Secret are required for Razorpay payment links", severity: "warning" });
      return;
    }
    setCreateSubmitting(true);
    const serviceId = SERVICE_ID();
    PaymentConfigService.create(serviceId, {
      usage_scope: usageScope,
      provider_name,
      configuration_name: configuration_name.trim(),
      purpose_code: purpose_code || "00",
      merchant_category_code: merchant_category_code || "0000",
      status: usageScope === USAGE_PAYMENT_LINK ? "Active" : status || "Needs_Connecting",
      merchant_vpa: provider_name === "upi_vpa" ? merchant_vpa.trim() : undefined,
      data_endpoint_url: usageScope === USAGE_ORDER_DETAILS ? data_endpoint_url?.trim() || undefined : undefined,
      key_id: usageScope === USAGE_PAYMENT_LINK && provider_name === "razorpay" ? key_id.trim() : undefined,
      key_secret: usageScope === USAGE_PAYMENT_LINK && provider_name === "razorpay" ? key_secret.trim() : undefined,
    })
      .then((res) => {
        const result = res?.data !== undefined ? res.data : res;
        setSnackbar({ open: true, message: "Payment configuration created successfully.", severity: "success" });
        setCreateOpen(false);
        if (result?.oauth_url) {
          setSnackbar({
            open: true,
            message: "Open the OAuth link to complete Razorpay connection.",
            severity: "info",
          });
          window.open(result.oauth_url, "_blank");
        }
        fetchList();
      })
      .catch((err) => {
        const errorData = err?.response?.data?.error?.data;
        if (errorData?.terms_of_service_pending) {
          setTermsOfServiceDialog({
            open: true,
            message: err?.response?.data?.error?.message || "WhatsApp Payments Terms of Service must be accepted before adding a payment configuration.",
            link: errorData.terms_of_service_link || null,
          });
        } else {
          const msg = err?.response?.data?.error?.message || err?.message || "Create failed";
          setSnackbar({ open: true, message: msg, severity: "error" });
        }
      })
      .finally(() => setCreateSubmitting(false));
  };

  const handleSetActive = (config) => {
    const serviceId = SERVICE_ID();
    if (!serviceId) return;
    setActionLoading(config.id);
    PaymentConfigService.setActive(serviceId, config.id)
      .then(() => {
        setSnackbar({ open: true, message: "Active payment configuration updated.", severity: "success" });
        fetchList();
      })
      .catch((err) => {
        setSnackbar({ open: true, message: err?.response?.data?.error?.message || "Failed to set active", severity: "error" });
      })
      .finally(() => setActionLoading(null));
  };

  const handleRegenerateOpen = (config) => {
    setRegenerateConfig(config);
    setRegenerateOpen(true);
  };

  const handleRegenerateSubmit = () => {
    if (!regenerateConfig) return;
    const serviceId = SERVICE_ID();
    setRegenerateSubmitting(true);
    PaymentConfigService.regenerateOAuth(serviceId, regenerateConfig.configuration_name)
      .then((res) => {
        setSnackbar({ open: true, message: "OAuth link regenerated. Complete the link to reconnect.", severity: "success" });
        setRegenerateOpen(false);
        setRegenerateConfig(null);
        if (res?.oauth_url) window.open(res.oauth_url, "_blank");
        fetchList();
      })
      .catch((err) => {
        setSnackbar({ open: true, message: err?.response?.data?.error?.message || "Regenerate failed", severity: "error" });
      })
      .finally(() => setRegenerateSubmitting(false));
  };

  const handleDataEndpointOpen = (config) => {
    setDataEndpointConfig(config);
    setDataEndpointUrl(config.data_endpoint_url || "");
    setDataEndpointOpen(true);
  };

  const handleDataEndpointSubmit = () => {
    if (!dataEndpointConfig) return;
    const serviceId = SERVICE_ID();
    setDataEndpointSubmitting(true);
    PaymentConfigService.updateDataEndpoint(serviceId, dataEndpointConfig.configuration_name, dataEndpointUrl.trim() || undefined)
      .then(() => {
        setSnackbar({ open: true, message: "Data endpoint updated.", severity: "success" });
        setDataEndpointOpen(false);
        setDataEndpointConfig(null);
        fetchList();
      })
      .catch((err) => {
        setSnackbar({ open: true, message: err?.response?.data?.error?.message || "Update failed", severity: "error" });
      })
      .finally(() => setDataEndpointSubmitting(false));
  };

  const handleRemove = (config) => {
    setRemoveConfirm(config);
  };

  const handleEditCredentialsOpen = (config) => {
    const provider = String(config?.provider_name || config?.type || "").toLowerCase();
    const usageScope = String(config?.usage_scope || USAGE_ORDER_DETAILS).toLowerCase();
    if (provider !== "razorpay" || usageScope !== USAGE_PAYMENT_LINK) return;
    setEditCredentialsConfig(config);
    setEditCredentialsForm({
      key_id: config?.key_id || "",
      key_secret: config?.key_secret || "",
    });
    setEditCredentialsOpen(true);
  };

  const handleEditCredentialsSave = () => {
    if (!editCredentialsConfig) return;
    const serviceId = SERVICE_ID();
    const keyId = String(editCredentialsForm.key_id || "").trim();
    const keySecret = String(editCredentialsForm.key_secret || "").trim();
    if (!keyId || !keySecret) {
      setSnackbar({ open: true, message: "Key ID and Key Secret are required", severity: "warning" });
      return;
    }
    setEditCredentialsSubmitting(true);
    PaymentConfigService.updateCredentials(serviceId, editCredentialsConfig.id, keyId, keySecret)
      .then(() => {
        setSnackbar({ open: true, message: "Razorpay credentials updated.", severity: "success" });
        setEditCredentialsOpen(false);
        setEditCredentialsConfig(null);
        fetchList();
      })
      .catch((err) => {
        setSnackbar({ open: true, message: err?.response?.data?.error?.message || "Update failed", severity: "error" });
      })
      .finally(() => setEditCredentialsSubmitting(false));
  };

  const handleRemoveConfirm = () => {
    if (!removeConfirm) return;
    const serviceId = SERVICE_ID();
    setActionLoading(removeConfirm.id);
    PaymentConfigService.remove(serviceId, removeConfirm.configuration_name)
      .then(() => {
        setSnackbar({ open: true, message: "Payment configuration removed.", severity: "success" });
        setRemoveConfirm(null);
        fetchList();
      })
      .catch((err) => {
        setSnackbar({ open: true, message: err?.response?.data?.error?.message || "Remove failed", severity: "error" });
      })
      .finally(() => setActionLoading(null));
  };

  if (!SERVICE_ID()) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="text.secondary">No service selected. Please select a store first.</Typography>
      </Box>
    );
  }
  const visibleProviderOptions = createForm.usage_scope === USAGE_PAYMENT_LINK
    ? PROVIDER_OPTIONS.filter((opt) => ["razorpay", "upi_vpa"].includes(opt.value))
    : PROVIDER_OPTIONS;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="subtitle1" color="text.secondary">
          Configure native WhatsApp payments and payment-link credentials separately.
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateOpen}>
          Add configuration
        </Button>
      </Box>
      <Alert severity={canUseOrderDetails ? "info" : "warning"} sx={{ mb: 2 }}>
        {canUseOrderDetails
          ? "New API-number onboarding can use Meta native order details and payment links."
          : "WhatsApp Business App onboarding does not support Meta native order details. Configure payment-link credentials instead."}
      </Alert>

      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : list.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 3 }}>
              No payment configurations yet. Add one to receive payments on WhatsApp.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Used for</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell>Provider MID</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {list.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    onClick={() => handleEditCredentialsOpen(row)}
                    sx={{
                      cursor: String(row?.provider_name || row?.type || "").toLowerCase() === "razorpay"
                        && String(row?.usage_scope || USAGE_ORDER_DETAILS).toLowerCase() === USAGE_PAYMENT_LINK
                        ? "pointer"
                        : "default",
                    }}
                  >
                    <TableCell>{row.configuration_name}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={(row.usage_scope || USAGE_ORDER_DETAILS) === USAGE_PAYMENT_LINK ? "Payment link" : "Native order details"}
                        color={(row.usage_scope || USAGE_ORDER_DETAILS) === USAGE_PAYMENT_LINK ? "secondary" : "primary"}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={PROVIDER_OPTIONS.find((p) => p.value === (row.provider_name || row.type))?.label || row.provider_name || row.type || "—"}
                        color={GATEWAY_PROVIDERS.includes(row.type || row.provider_name) ? "primary" : "default"}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.provider_mid || "—"}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={row.status || "—"}
                        color={row.status === "Active" ? "success" : "default"}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {row.active_status === "active" ? (
                        <Chip size="small" label="Active" color="success" />
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={!!actionLoading}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSetActive(row);
                          }}
                        >
                          {actionLoading === row.id ? <CircularProgress size={16} /> : "Set active"}
                        </Button>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {GATEWAY_PROVIDERS.includes(row.type || row.provider_name)
                      && String(row.usage_scope || USAGE_ORDER_DETAILS).toLowerCase() === USAGE_ORDER_DETAILS && (
                        <>
                          <Tooltip title="Regenerate OAuth link">
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleRegenerateOpen(row); }}>
                              <RefreshIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Data endpoint URL">
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDataEndpointOpen(row); }}>
                              <LinkIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      {String(row.provider_name || row.type || "").toLowerCase() === "razorpay"
                      && String(row.usage_scope || USAGE_ORDER_DETAILS).toLowerCase() === USAGE_PAYMENT_LINK && (
                        <Tooltip title="Edit Razorpay payment-link keys">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEditCredentialsOpen(row); }}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Remove">
                        <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleRemove(row); }}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add payment configuration</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label="Configuration name"
              value={createForm.configuration_name}
              onChange={(e) => setCreateForm((f) => ({ ...f, configuration_name: e.target.value }))}
              placeholder="e.g. my-store-payments"
              fullWidth
              required
            />
            <FormControl fullWidth required>
              <InputLabel>Use this for</InputLabel>
              <Select
                value={createForm.usage_scope}
                label="Use this for"
                onChange={(e) => {
                  const nextScope = e.target.value;
                  setCreateForm((f) => ({
                    ...f,
                    usage_scope: nextScope,
                    provider_name: nextScope === USAGE_PAYMENT_LINK && !["razorpay", "upi_vpa"].includes(f.provider_name)
                      ? "razorpay"
                      : f.provider_name,
                  }));
                }}
              >
                <MenuItem value={USAGE_PAYMENT_LINK}>Payment links</MenuItem>
                <MenuItem value={USAGE_ORDER_DETAILS} disabled={!canUseOrderDetails}>
                  Meta native order details
                </MenuItem>
              </Select>
            </FormControl>
            {createForm.usage_scope === USAGE_PAYMENT_LINK ? (
              <Alert severity="info">
                Use this for chatbot Payment Link nodes, Business App onboarding, booking flows, and CRM-only businesses.
              </Alert>
            ) : (
              <Alert severity="info">
                Use this only for new API numbers that support WhatsApp native review and pay.
              </Alert>
            )}
            <FormControl fullWidth required>
              <InputLabel>Provider name</InputLabel>
              <Select
                value={createForm.provider_name}
                label="Provider name"
                onChange={(e) => setCreateForm((f) => ({ ...f, provider_name: e.target.value }))}
              >
                {visibleProviderOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {createForm.usage_scope === USAGE_ORDER_DETAILS && (
              <>
                <FormControl fullWidth required>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={createForm.status}
                    label="Status"
                    onChange={(e) => setCreateForm((f) => ({ ...f, status: e.target.value }))}
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Purpose code"
                  value={createForm.purpose_code}
                  onChange={(e) => setCreateForm((f) => ({ ...f, purpose_code: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label="Merchant category code"
                  value={createForm.merchant_category_code}
                  onChange={(e) => setCreateForm((f) => ({ ...f, merchant_category_code: e.target.value }))}
                  fullWidth
                />
              </>
            )}
            {createForm.provider_name === "upi_vpa" && (
              <TextField
                label="Merchant UPI VPA"
                value={createForm.merchant_vpa}
                onChange={(e) => setCreateForm((f) => ({ ...f, merchant_vpa: e.target.value }))}
                placeholder="merchant@upi"
                fullWidth
                required
              />
            )}
            {createForm.usage_scope === USAGE_ORDER_DETAILS && GATEWAY_PROVIDERS.includes(createForm.provider_name) && (
              <TextField
                label="Data endpoint URL (optional)"
                value={createForm.data_endpoint_url}
                onChange={(e) => setCreateForm((f) => ({ ...f, data_endpoint_url: e.target.value }))}
                placeholder="https://..."
                fullWidth
              />
            )}
            {createForm.usage_scope === USAGE_PAYMENT_LINK && createForm.provider_name === "razorpay" && (
              <>
                <TextField
                  label="Key ID"
                  value={createForm.key_id}
                  onChange={(e) => setCreateForm((f) => ({ ...f, key_id: e.target.value }))}
                  fullWidth
                  required
                />
                <TextField
                  label="Key Secret"
                  type="password"
                  value={createForm.key_secret}
                  onChange={(e) => setCreateForm((f) => ({ ...f, key_secret: e.target.value }))}
                  fullWidth
                  required
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateSubmit} disabled={createSubmitting}>
            {createSubmitting ? <CircularProgress size={20} /> : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Razorpay credentials */}
      <Dialog open={editCredentialsOpen} onClose={() => setEditCredentialsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Razorpay credentials</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {editCredentialsConfig?.configuration_name || "Razorpay configuration"}
            </Typography>
            <TextField
              label="Key ID"
              value={editCredentialsForm.key_id}
              onChange={(e) => setEditCredentialsForm((f) => ({ ...f, key_id: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Key Secret"
              type="password"
              value={editCredentialsForm.key_secret}
              onChange={(e) => setEditCredentialsForm((f) => ({ ...f, key_secret: e.target.value }))}
              fullWidth
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditCredentialsOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditCredentialsSave} disabled={editCredentialsSubmitting}>
            {editCredentialsSubmitting ? <CircularProgress size={20} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Regenerate OAuth dialog */}
      <Dialog open={regenerateOpen} onClose={() => setRegenerateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Regenerate OAuth link</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            A new link will be generated to reconnect this payment gateway. You will be redirected to https://ws.orkaapps.com after completing the flow.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegenerateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleRegenerateSubmit} disabled={regenerateSubmitting}>
            {regenerateSubmitting ? <CircularProgress size={20} /> : "Regenerate"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Data endpoint dialog */}
      <Dialog open={dataEndpointOpen} onClose={() => setDataEndpointOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Data endpoint URL</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            URL for coupons, shipping address and real-time inventory in Checkout Button Templates.
          </Typography>
          <TextField
            label="Data endpoint URL"
            value={dataEndpointUrl}
            onChange={(e) => setDataEndpointUrl(e.target.value)}
            fullWidth
            placeholder="https://..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDataEndpointOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleDataEndpointSubmit} disabled={dataEndpointSubmitting}>
            {dataEndpointSubmitting ? <CircularProgress size={20} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove confirm */}
      <Dialog open={!!removeConfirm} onClose={() => setRemoveConfirm(null)}>
        <DialogTitle>Remove payment configuration?</DialogTitle>
        <DialogContent>
          <Typography>
            This will remove &quot;{removeConfirm?.configuration_name}&quot; from WhatsApp. You can add it again later.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveConfirm(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleRemoveConfirm} disabled={!!actionLoading}>
            {actionLoading ? <CircularProgress size={20} /> : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Terms of Service pending */}
      <Dialog
        open={termsOfServiceDialog.open}
        onClose={() => setTermsOfServiceDialog((s) => ({ ...s, open: false }))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Accept WhatsApp Payments Terms</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {termsOfServiceDialog.message}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Open the link below to accept the Terms of Service, then try adding the payment configuration again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTermsOfServiceDialog((s) => ({ ...s, open: false }))}>
            Close
          </Button>
          {termsOfServiceDialog.link && (
            <Button
              variant="contained"
              href={termsOfServiceDialog.link}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setTermsOfServiceDialog((s) => ({ ...s, open: false }))}
            >
              Accept Terms of Service
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
